// src/pages/ResetPasswordPage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValid(false);
      return;
    }
    api.get(`/auth/staff/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(res => setValid(res.data.valid === true))
      .catch(() => setValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/staff/reset-password', { token, newPassword });
      setDone(true);
      toast.success('Password reset!');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--navy)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: 'white', marginBottom: 6 }}>NJJBA</h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
            Chamber Management
          </p>
        </div>

        <div className="card" style={{ padding: '32px 36px' }}>
          {validating ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div className="spinner" />
              <p style={{ marginTop: 12, color: 'var(--gray-500)' }}>Checking your reset link…</p>
            </div>
          ) : !valid ? (
            <>
              <div style={{ textAlign: 'center', marginBottom: 16, fontSize: '3rem' }}>⚠️</div>
              <h2 style={{ marginBottom: 12, color: 'var(--navy)', fontFamily: 'var(--font-serif)', textAlign: 'center' }}>Link Expired</h2>
              <p style={{ fontSize: '0.9rem', color: 'var(--gray-600)', textAlign: 'center', marginBottom: 20 }}>
                This password reset link is invalid or has expired. Reset links expire after 1 hour.
              </p>
              <Link to="/forgot-password" className="btn btn-primary w-full" style={{ justifyContent: 'center' }}>
                Request a New Link
              </Link>
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Link to="/login" style={{ fontSize: '0.85rem', color: 'var(--gray-500)', textDecoration: 'none' }}>
                  ← Back to sign in
                </Link>
              </div>
            </>
          ) : done ? (
            <>
              <div style={{ textAlign: 'center', marginBottom: 16, fontSize: '3rem' }}>✅</div>
              <h2 style={{ marginBottom: 12, color: 'var(--navy)', fontFamily: 'var(--font-serif)', textAlign: 'center' }}>Password Reset</h2>
              <p style={{ fontSize: '0.9rem', color: 'var(--gray-600)', textAlign: 'center', marginBottom: 20 }}>
                Your password has been updated. Redirecting you to sign in…
              </p>
            </>
          ) : (
            <>
              <h2 style={{ marginBottom: 8, color: 'var(--navy)', fontFamily: 'var(--font-serif)' }}>Set New Password</h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: 20 }}>
                Choose a new password (at least 8 characters).
              </p>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    autoFocus
                    required
                    minLength={8}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                </div>
                <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading} style={{ marginTop: 8, justifyContent: 'center' }}>
                  {loading ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Updating...</> : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', marginTop: 24 }}>
          New Jersey Jewish Business Alliance · portal.njjba.com
        </p>
      </div>
    </div>
  );
}
